import React from "react";
import useModal from "@ui/lib/hooks/useModal";
import ApplicationMenu from "./ApplicationMenu";
import Modal from "@ui/Modal";
import t from "../../../i18n";
import { useIndexedProjects } from "../../../api/useProjects";
import AppHeaderTitle from "./Title";
import AppHeaderIcon from "./Icon";
import HeaderDropdownMenu from "../../Project/HeaderDropdownMenu/HeaderDropdownMenu";
import AppHeaderActions from "./Actions";
import { useUser } from "../../../api/useUser";
import ChangeUserNameModalForm from "../../Administration/Users/ChangeUserNameModalForm";
import { Settings5Line } from "@ui/IconV2/react";
import Badge from "@ui/Badge";
import { getBadgeStableColors } from "@ui/Badge/utils";
import SetupModeBanner from "../../SetupMode/SetupModeBanner";
import ApplicationHeader from "@ui/ApplicationHeader";
import HamburgerMenu from "@ui/HamburgerMenu";
import { css } from "@linaria/core";
import ProjectSpinner from "./ProjectSpinner";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { colors } from "@ui/Theme";
import { useLocalExternalLink } from "../../../api/useExternalLinks";
import { useMatch, useNavigate, useParams } from "react-router";
import { useHandle } from "../../../router/helpers/handle";
import { toPath } from "../../../router/helpers/path";
import UserGuideButton from "./UserGuideButton";
import { ErrorBoundary } from "react-error-boundary";

export const AppHeader = () => {
  const {
    modal: userModalOpened,
    closeModal: closeUserModal,
    openModal: openUserModal,
  } = useModal();

  const { projectId } = useParams();
  const navigate = useNavigate();
  const projects = useIndexedProjects();
  const project = projectId ? projects[projectId] : undefined;
  const navigation = useHandle("navigation");
  const user = useUser();

  const localExternalLinks = useLocalExternalLink();

  const settingsMatch = useMatch({ path: "/projects/:projectId/settings", end: false });
  const setupMatch = useMatch({ path: "/projects/:projectId/setup", end: false });
  const isSettingsPage = Boolean(settingsMatch || setupMatch);

  return (
    <>
      <Modal
        title={t("users.modal.update.update_user")}
        isOpen={userModalOpened}
        onRequestClose={closeUserModal}
      >
        <ChangeUserNameModalForm onRequestClose={closeUserModal} />
      </Modal>
      <ApplicationHeader
        withPortal={window.__webpack_public_path__ !== "/"}
        viewTitle={
          <>
            {project ? (
              <ErrorBoundary key={project.id} fallbackRender={() => null}>
                <React.Suspense fallback={null}>
                  <ProjectSpinner project={project} />
                </React.Suspense>
              </ErrorBoundary>
            ) : null}
            <AppHeaderTitle />
            {project && <AppHeaderIcon project={project} />}
          </>
        }
        viewTitleExtra={
          <>
            {project && (project.siteId || project.typeId) ? (
              <div className={badgesContainer}>
                {project.siteId && (
                  <Badge style={getBadgeStableColors(project.siteId)}>{project.siteId}</Badge>
                )}
                {project.typeId && <Badge>{project.typeId}</Badge>}
              </div>
            ) : null}
            {project && (
              <div className={projectQuickNavContainer}>
                <ButtonIconV2
                  className={projectSettingsButton}
                  theme="dark"
                  checked={isSettingsPage}
                  aria-label={t("project.document.title.settings")}
                  aria-current={isSettingsPage ? "page" : undefined}
                  onClick={() =>
                    void navigate(
                      toPath("/projects/:projectId/settings", { projectId: project.id }),
                    )
                  }
                >
                  <Settings5Line size="m" />
                </ButtonIconV2>
                <HeaderDropdownMenu project={project} />
              </div>
            )}
          </>
        }
        viewNavigation={navigation?.()}
        viewAction={
          <>
            {project && <AppHeaderActions project={project} />}

            <UserGuideButton />
            <HamburgerMenu>
              <ApplicationMenu
                externalLinks={localExternalLinks}
                openUserModal={openUserModal}
                user={user}
              />
            </HamburgerMenu>
          </>
        }
      />
      <SetupModeBanner />
    </>
  );
};

//#region CSS
const badgesContainer = css`
  align-items: center;
  display: flex;
  gap: 4px;
`;
const projectQuickNavContainer = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;
const projectSettingsButton = css`
  &&.checked:not([disabled]) {
    color: ${colors.hydrogen};

    &::before {
      background-color: ${colors.lightNitrogen300};
      opacity: 0.6;
    }

    &:hover::before {
      background-color: ${colors.lightNitrogen300};
    }
  }
`;
//#endregion CSS
